import React from "react";


/**
 * BrandLogo — the AI Music Maker neon mark.
 *
 * A rounded squircle with a stylised 5-bar neon equalizer that reads as
 * both "equalizer" and a subtle "M". Uses the landing gradient palette
 * (emerald → cyan → blue) with an outer glow for the neon vibe.
 *
 * Accepts size (px) and optional className. Unique IDs per instance so
 * multiple logos on one page don't stomp on each other's <defs>.
 */
let _uid = 0;

export default function BrandLogo({ size = 32, className = "brand-logo-img", title = "AI Music Maker" }) {
  const id = React.useMemo(() => `bl${++_uid}`, []);
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
    >
      <title>{title}</title>

      <defs>
        {/* Deep teal-black squircle bg with a subtle corner glow */}
        <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#0b1520" />
          <stop offset="1" stopColor="#040a12" />
        </linearGradient>
        {/* Inner corner emerald glow */}
        <radialGradient id={`${id}-bgGlow`} cx="12" cy="12" r="28" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#10b981" stopOpacity="0.32" />
          <stop offset="1" stopColor="#10b981" stopOpacity="0" />
        </radialGradient>
        {/* Bar gradient: emerald → cyan → sky */}
        <linearGradient id={`${id}-bars`} x1="0" y1="8" x2="0" y2="40" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#a7f3d0" />
          <stop offset="0.45" stopColor="#22d3ee" />
          <stop offset="1" stopColor="#3b82f6" />
        </linearGradient>
        {/* Neon outer glow filter */}
        <filter id={`${id}-glow`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Background squircle */}
      <rect x="0" y="0" width="48" height="48" rx="12" fill={`url(#${id}-bg)`} />
      <rect x="0" y="0" width="48" height="48" rx="12" fill={`url(#${id}-bgGlow)`} />
      {/* Subtle inner stroke for crispness */}
      <rect x="0.5" y="0.5" width="47" height="47" rx="11.5" fill="none" stroke="rgba(255,255,255,0.06)" />

      {/* 5-bar equalizer — short / tall / tallest / tall / short.
          The middle three are centred around y=24 so the mark reads as a rhythm pulse.
          Bars are pill-shaped (rx=2) with a shared emerald→cyan→blue gradient and a glow.
          Each bar has an `eq-bar` class + index so CSS can stagger a pulse on hover. */}
      <g filter={`url(#${id}-glow)`} fill={`url(#${id}-bars)`} className="eq-bars">
        <rect className="eq-bar eq-bar-1" x="8"  y="19" width="4" height="10" rx="2" />
        <rect className="eq-bar eq-bar-2" x="15" y="13" width="4" height="22" rx="2" />
        <rect className="eq-bar eq-bar-3" x="22" y="9"  width="4" height="30" rx="2" />
        <rect className="eq-bar eq-bar-4" x="29" y="13" width="4" height="22" rx="2" />
        <rect className="eq-bar eq-bar-5" x="36" y="19" width="4" height="10" rx="2" />
      </g>

      {/* Accent beat dot — a bright cyan spark above the tallest bar for the "AI spark" vibe */}
      <circle className="eq-spark" cx="24" cy="6" r="1.6" fill="#e0fbff" opacity="0.95" />
      <circle className="eq-spark-glow" cx="24" cy="6" r="3.2" fill="#22d3ee" opacity="0.25" />
    </svg>
  );
}
